import Sentry from "@similarweb/sentry-logger";

import { NO_DATA_SIGN } from "app/filters/const";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { translate } from "app/filters/i18nFilter";

import { SENTIMENT_COLOR_SET } from "../../../shared/LLMConsts";

import type { Brand, FlattenedBrandScores, IBrandComparison, ICompareSentiment } from "./types";

const hasNotSentimentValues = (value: number): boolean => !value && value !== 0;

const hasNoSentimentBreakdown = (sentimentBreakdown: ICompareSentiment): boolean => {
    return (
        !sentimentBreakdown ||
        typeof sentimentBreakdown !== "object" ||
        Object.values(sentimentBreakdown).every(hasNotSentimentValues)
    );
};

const createHeatmapCellValue = (
    score: number | undefined,
    sentimentBreakdown: ICompareSentiment | undefined,
    topicTitle: string,
) => {
    if (hasNoSentimentBreakdown(sentimentBreakdown)) {
        return score;
    }
    const { positive, neutral, negative } = sentimentBreakdown;
    return {
        value: score,
        tooltipConfig: {
            valueFormatter: (value: number) =>
                hasNotSentimentValues(value) ? NO_DATA_SIGN : (abbrNumberFilter()(value) as string),
            title: topicTitle,
            rows: [
                {
                    label: translate("llm.sentiment_report.brand_comparison.tooltip.positive"),
                    value: positive,
                    color: SENTIMENT_COLOR_SET.positive,
                },
                {
                    label: translate("llm.sentiment_report.brand_comparison.tooltip.neutral"),
                    value: neutral,
                    color: SENTIMENT_COLOR_SET.neutral,
                },
                {
                    label: translate("llm.sentiment_report.brand_comparison.tooltip.negative"),
                    value: negative,
                    color: SENTIMENT_COLOR_SET.negative,
                },
            ],
        },
    };
};

export const transformToHeatmapRows = (
    rows: IBrandComparison[] | undefined,
    selectedBrands: Brand[],
): (Pick<IBrandComparison, "topic"> & FlattenedBrandScores)[] => {
    if (!rows || rows.length === 0) {
        return [];
    }
    try {
        return rows.map((row) => {
            const flattenedBrandScores: FlattenedBrandScores = {};
            const myBrandsData = row.myBrands?.find((b) => b.brandName === "aggr");
            flattenedBrandScores["myBrands"] = createHeatmapCellValue(
                myBrandsData?.score,
                myBrandsData?.sentimentBreakdown,
                row.topic ?? "",
            );

            selectedBrands.forEach((brand) => {
                const brandData = row.brands?.find((b) => b.brandName === brand);
                flattenedBrandScores[brand] = createHeatmapCellValue(
                    brandData?.score,
                    brandData?.sentimentBreakdown,
                    row.topic ?? "",
                );
            });

            return {
                topic: row.topic,
                ...flattenedBrandScores,
            } as unknown as Pick<IBrandComparison, "topic"> & FlattenedBrandScores;
        });
    } catch (error) {
        Sentry.errorTracker({
            message: "Error transforming to heatmap rows",
            e: error,
            level: "error",
        });
        return [];
    }
};

export const formatCellValue = (value: number) => (!value && value !== 0 ? NO_DATA_SIGN : value.toFixed(2));
